import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, X } from 'phosphor-react';
import { ref, get } from 'firebase/database';
import { db } from '../firebase';
import { dlog } from '../utils/debug';
import {
  queryGeoPermission,
  getCurrentLocation,
  computeVenueIdGrid,
} from '../utils/geo';
import './GeoBanner.css';

/**
 * Quiet inline discovery row at the top of the landing list.
 *
 * Three states drive copy + visual weight; the banner navigates to
 * /nearby on tap regardless of state. The /nearby page handles the
 * permission prompt flow when needed.
 *
 *   - 'prompt' (haven't asked yet):     "Find matches near you"
 *   - 'granted' + ≥1 nearby:            "N matches near you"
 *   - granted+0, denied, unsupported:   null (no row)
 *
 * Intentionally NOT a card. No shadow, no background — just text on
 * the page so it integrates with the chrome instead of competing
 * with the match rows below. One subtle bottom divider establishes
 * it as a navigation surface without shouting.
 *
 * Props:
 *   - currentUserUid: string | null — skip the user's own matches in
 *     the nearby count (this is "discovery" of others' matches).
 */
function GeoBanner({ currentUserUid = null }) {
  const navigate = useNavigate();
  // 'checking' | 'prompt' | 'granted' | 'denied' | 'unsupported'
  const [permission, setPermission] = useState('checking');
  const [nearbyCount, setNearbyCount] = useState(0);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('geoBannerDismissed') === '1'
  );

  // Peek at permission state on mount. If granted, ambient-fetch the
  // nearby count so the row can show "N near you" without a tap.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await queryGeoPermission();
      if (cancelled) return;
      setPermission(p === 'unsupported' ? 'unsupported' : p);
      if (p !== 'granted') return;
      // Already granted — quietly fetch count.
      try {
        const loc = await getCurrentLocation({ maximumAge: 5 * 60 * 1000 });
        if (cancelled) return;
        const grid = new Set(computeVenueIdGrid(loc.lat, loc.lng));
        const snapshot = await get(ref(db, 'matches'));
        if (cancelled) return;
        let count = 0;
        if (snapshot.exists()) {
          snapshot.forEach((child) => {
            const m = child.val();
            if (!m || !m.venueId) return;
            if (!grid.has(m.venueId)) return;
            if (m.creatorId && m.creatorId === currentUserUid) return;
            count += 1;
          });
        }
        setNearbyCount(count);
        dlog(`GeoBanner: ambient count ${count}`);
      } catch (err) {
        // Silent — banner just hides in the granted+0 state below.
        dlog('GeoBanner ambient fetch failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUserUid]);

  const handleClick = useCallback(() => {
    navigate('/nearby');
  }, [navigate]);

  const handleDismiss = useCallback((e) => {
    e.stopPropagation();
    sessionStorage.setItem('geoBannerDismissed', '1');
    setDismissed(true);
  }, []);

  // Hide states: nothing useful to show.
  if (dismissed) return null;
  if (permission === 'checking') return null;
  if (permission === 'denied' || permission === 'unsupported') return null;
  if (permission === 'granted' && nearbyCount === 0) return null;

  const isActive = permission === 'granted' && nearbyCount > 0;

  return (
    <div className={`geo-banner ${isActive ? 'geo-banner--active' : 'geo-banner--prompt'}`}>
      <button
        type="button"
        className="geo-banner-body"
        onClick={handleClick}
      >
        <MapPin
          size={18}
          weight="fill"
          className="geo-banner-icon"
          aria-hidden="true"
        />
        <span className="geo-banner-text">
          {isActive
            ? `${nearbyCount} match${nearbyCount !== 1 ? 'es' : ''} near you`
            : 'Find matches near you'}
        </span>
      </button>
      <button
        type="button"
        aria-label="Dismiss"
        className="geo-banner-dismiss"
        onClick={handleDismiss}
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}

export default GeoBanner;
